import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client';
import type { DeepPartial } from '@apollo/client/utilities';
import { ApolloProvider } from '@apollo/client/react';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';
import { createNetworkStatusNotifier } from 'react-apollo-network-status';
import { useTranslation } from 'react-i18next';
import { useSignOut } from 'src/api/provider';
import { Maybe } from 'src/graphql/lib';
import Cookies from 'universal-cookie';
import { from, mergeMap, throwError } from 'rxjs';
import 'whatwg-fetch';
import { API_HOST, CSRF_COOKIE_NAME } from '../constants/environment';
import {
    ACCESS_TOKEN_KEY,
    ACCESS_TOKEN_SOURCE_KEY,
    IMPERSONATED_KEY,
} from '../constants/sessionStorage';
import { FieldErrors } from './errorHandling';

const hasStatusCode = (value: unknown): value is { statusCode: number } =>
    typeof value === 'object' && value !== null && 'statusCode' in value;
const cookies = new Cookies();

type RefreshTokenResponse = {
    access?: string;
};

type RefreshResult = {
    ok: boolean;
    accessToken: string | null;
};

let refreshInFlight: Promise<RefreshResult> | null = null;
const refreshSession = async (): Promise<RefreshResult> => {
    try {
        const headers: Record<string, string> = {};
        const csrfToken = cookies.get(CSRF_COOKIE_NAME);
        if (typeof csrfToken === 'string' && csrfToken) {
            headers['X-CSRFToken'] = csrfToken;
        }
        const response = await fetch(`${API_HOST}/api/auth/token/refresh/`, {
            method: 'POST',
            credentials: 'include',
            headers,
        });
        if (!response.ok) {
            return { ok: false, accessToken: null };
        }

        const data = (await response.json().catch(() => ({}))) as RefreshTokenResponse;
        const accessToken = typeof data.access === 'string' ? data.access : null;
        return { ok: true, accessToken };
    } catch {
        return { ok: false, accessToken: null };
    }
};

const ensureFreshSession = (): Promise<RefreshResult> => {
    if (!refreshInFlight) {
        refreshInFlight = refreshSession().finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
};

function useApolloClient(
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'],
) {
    const { t } = useTranslation('validation');
    const { enqueueSnackbar } = useSnackbar();
    const signOut = useSignOut();

    // useCallback required - showError is in useMemo deps for ApolloClient creation.
    // Without stable reference, Apollo client recreates every render → infinite loop.
    const showError = useCallback(
        (message: string | string[]) => {
            if (Array.isArray(message)) {
                message = message.join(' ');
            }
            enqueueSnackbar(message, { variant: 'error' });
        },
        [enqueueSnackbar],
    );

    // Keep useMemo here - ApolloClient is a third-party library that requires stable reference
    const apolloClient = useMemo(() => {
        const cache = new InMemoryCache({
            possibleTypes: {},
            typePolicies: {
                // === Types without ID fields that return signed URLs ===
                // These types have no id field and return URLs with changing expiration
                // timestamps. Without these policies, Apollo sees each URL as "new data"
                // and triggers infinite refetches.
                ImageType: {
                    keyFields: false,
                    merge: true,
                },
                FileType: {
                    keyFields: false,
                    merge: true,
                },

                // === Types with 'key' field that can be used as identifier ===
                // These types have a stable 'key' field that can identify them uniquely.
                LanguageType: {
                    keyFields: ['key'],
                },

                // === Profile types fetched with different field selections ===
                // These types are commonly fetched from multiple queries with different
                // fields. Without merge: true, Apollo would lose fields between queries.
                VendorProfileNode: {
                    merge: true,
                },
                UserProfileNode: {
                    merge: true,
                },
                VendorPerformanceProfile: {
                    keyFields: false,
                    merge: true,
                },
                VendorBusinessProfileNode: {
                    merge: true,
                },

                // === Nested types commonly causing merge conflicts ===
                // These embedded types don't have IDs and are queried with different
                // field selections from different parts of the app.
                VendorScoreSummary: {
                    keyFields: false,
                    merge: true,
                },
                VendorPerformancePillarPercentiles: {
                    keyFields: false,
                    merge: true,
                },
                ReviewMetrics: {
                    keyFields: false,
                    merge: true,
                },
            },
        });

        const httpLink = new HttpLink({
            uri: `${API_HOST}/api/graphql/`,
            credentials: 'include',
            fetch: window.fetch,
        });

        const customHeadersLink = setContext((_, { headers }) => {
            const customHeaders: { [key: string]: string } = {
                'X-CSRFToken': cookies.get(CSRF_COOKIE_NAME),
            };
            const impersonated = sessionStorage.getItem(IMPERSONATED_KEY);
            if (impersonated) {
                customHeaders['X-Impersonate'] = impersonated;
            }
            const accessToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
            if (accessToken) {
                customHeaders['Authorization'] = `Bearer ${accessToken}`;
            }
            return {
                headers: {
                    ...headers,
                    ...customHeaders,
                },
                fetchOptions: {
                    referrerPolicy: 'no-referrer-when-downgrade',
                },
            };
        });

        const sentryBreadcrumbLink = new ApolloLink((operation, forward) => {
            Sentry.addBreadcrumb({
                category: 'gql',
                level: 'info',
                message: `Operation: ${operation.operationName}`,
            });

            return forward?.(operation) ?? null;
        });

        const errorLink = onError(({ error, operation, forward }) => {
            if (!error) {
                return;
            }

            if (CombinedGraphQLErrors.is(error)) {
                error.errors.forEach((graphQLError) => {
                    if (!graphQLError.extensions) {
                        showError(graphQLError.message);
                        return;
                    }
                    switch (graphQLError.extensions.code) {
                        case 'ValidationError':
                            const errorFields = graphQLError.extensions.fields as FieldErrors;
                            if ('__all__' in errorFields) {
                                showError(errorFields.__all__);
                            } else {
                                const errorMessage = Object.values(errorFields).flatMap(
                                    (error) => error,
                                );
                                showError(errorMessage || t('validationErrorMessage'));
                            }
                            break;
                        case 'AuthorizationError':
                            showError(t('unauthorizedErrorMessage'));
                            break;
                        case 'InternalServerError':
                        default:
                            showError(graphQLError.message);
                    }
                });
                return;
            }

            if (!hasStatusCode(error)) {
                showError(t('unknownErrorMessage'));
                return;
            }

            const errorWithStatus = error;
            const operationName = operation.operationName ?? '';

            if ([401, 403].includes(errorWithStatus.statusCode)) {
                const hasAccessToken = Boolean(sessionStorage.getItem(ACCESS_TOKEN_KEY));
                const isMagicLinkToken =
                    sessionStorage.getItem(ACCESS_TOKEN_SOURCE_KEY) === 'query';
                const ctx = operation.getContext();
                const didAttemptRefresh = Boolean(ctx.__didAttemptAuthRefresh);

                // Best-effort silent refresh:
                // - Cookie-based sessions: refreshes access cookie.
                // - Header-based sessions (e.g. sites API token in sessionStorage): updates `ACCESS_TOKEN_KEY`
                //   if the refresh endpoint returns `access`.
                if (!didAttemptRefresh) {
                    operation.setContext({ ...ctx, __didAttemptAuthRefresh: true });
                    return from(ensureFreshSession()).pipe(
                        mergeMap(({ ok, accessToken }) => {
                            if (ok) {
                                if (hasAccessToken) {
                                    if (accessToken) {
                                        sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
                                        sessionStorage.setItem(ACCESS_TOKEN_SOURCE_KEY, 'refresh');
                                    } else {
                                        // Refresh succeeded but did not return an access token; fall back to cookie auth.
                                        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
                                        sessionStorage.removeItem(ACCESS_TOKEN_SOURCE_KEY);
                                    }
                                }

                                return forward(operation);
                            }

                            // Refresh failed; treat this as unauthenticated.
                            if (
                                !['GetMe', 'GetFeatureFlags'].includes(operationName) &&
                                window.location.pathname !== '/login'
                            ) {
                                const redirectTo = isMagicLinkToken ? '/invalid-link' : undefined;
                                void signOut(redirectTo);
                            }
                            return throwError(() => error);
                        }),
                    );
                }

                // If we've already tried refreshing and still got 401/403, or we were using a one-time token,
                // fall back to signing out (except for unauthenticated bootstrapping queries).
                if (operationName === 'GetMe') {
                    return;
                }
                if (operationName === 'GetFeatureFlags') {
                    return;
                }
                if (window.location.pathname === '/login') {
                    return;
                }

                const redirectTo = isMagicLinkToken ? '/invalid-link' : undefined;
                void signOut(redirectTo);
                return;
            }

            if (errorWithStatus.statusCode >= 400 && errorWithStatus.statusCode < 500) {
                Sentry.captureException(errorWithStatus);
            }

            showError(t('unknownErrorMessage'));
        });

        return new ApolloClient({
            cache,

            link: ApolloLink.from([
                networkStatusNotifierLink,
                errorLink,
                customHeadersLink,
                sentryBreadcrumbLink,
                httpLink,
            ]),

            defaultOptions: {
                watchQuery: {
                    errorPolicy: 'all',
                    fetchPolicy: 'cache-and-network',
                    nextFetchPolicy: (lastFetchPolicy) => {
                        if (
                            lastFetchPolicy === 'cache-and-network' ||
                            lastFetchPolicy === 'network-only'
                        ) {
                            return 'cache-first';
                        }
                        return lastFetchPolicy;
                    },
                },
            },
        });
    }, [showError, t, networkStatusNotifierLink, signOut]);

    return apolloClient;
}

export interface GraphqlProviderProps {
    networkStatusNotifierLink: ReturnType<typeof createNetworkStatusNotifier>['link'];
    children: React.ReactNode;
}

export const GraphqlProvider = ({ networkStatusNotifierLink, children }: GraphqlProviderProps) => {
    const client = useApolloClient(networkStatusNotifierLink);
    return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export type Node = {
    id: string;
};

type Edge<TNode extends Node> = {
    node: Maybe<TNode>;
};

export type Connection<TNode extends Node> = {
    edges: Maybe<Edge<TNode>>[];
};

export type DeepPartialConnection<TNode extends Node> = DeepPartial<Connection<TNode>>;

export function getNodesFromConnection<TNode extends Node>(
    connection: Connection<TNode> | DeepPartialConnection<TNode> | null | undefined,
) {
    if (!connection) {
        return null;
    }
    const edges = (connection.edges ?? []) as (Edge<TNode> | null | undefined)[];
    return edges
        .map((edge) => edge?.node as TNode | null | undefined)
        .filter((node): node is TNode => Boolean(node));
}
